import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  CreditCard, Euro, Plus, Trash2, Wallet, FileCheck, TrendingUp,
} from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const TIPO_CLS = {
  Preventivo: "bg-primary/10 text-primary",
  Appuntamento: "bg-blue-100 text-blue-700",
  Manuale: "bg-slate-100 text-slate-600",
};

export default function Incassi() {
  const [preventivi, setPreventivi] = useState([]);
  const [data, setData] = useState({ movimenti: [], totale_incassato: 0, per_metodo: {} });
  const [clients, setClients] = useState([]);
  const [methods, setMethods] = useState([]);
  const [modes, setModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([api.get("/incassi/preventivi"), api.get("/incassi")]);
      setPreventivi(p.data);
      setData(m.data);
    } catch { toast.error("Errore caricamento incassi"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.get("/clients").then(({ data }) => setClients(data));
    api.get("/payment-methods").then(({ data }) => setMethods(data));
    api.get("/payment-modes").then(({ data }) => setModes(data));
  }, [load]);

  const today = () => new Date().toISOString().slice(0, 10);

  const openFromQuote = (q) => {
    setForm({ quote_id: q.id, client_id: q.client_id, cliente_nome: q.cliente_nome, numero: q.numero,
              importo: q.residuo > 0 ? q.residuo : q.totale, metodo_pagamento: q.metodo_pagamento || "",
              modalita_pagamento: q.modalita_pagamento || "", data: today(), note: "" });
    setOpen(true);
  };
  const openManual = () => {
    setForm({ quote_id: null, client_id: "", cliente_nome: "", numero: "", importo: 0,
              metodo_pagamento: "", modalita_pagamento: "", data: today(), note: "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.quote_id && !form.client_id) { toast.error("Seleziona un paziente"); return; }
    if (!(parseFloat(form.importo) > 0)) { toast.error("Inserisci un importo valido"); return; }
    setSaving(true);
    try {
      await api.post("/incassi", {
        quote_id: form.quote_id || null, client_id: form.client_id || null,
        importo: parseFloat(form.importo), metodo_pagamento: form.metodo_pagamento,
        modalita_pagamento: form.modalita_pagamento, data: new Date(form.data).toISOString(), note: form.note,
      });
      toast.success("Incasso registrato");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/incassi/${id}`); toast.success("Movimento eliminato"); load(); }
    catch { toast.error("Errore eliminazione"); }
  };

  const residuoTotale = preventivi.reduce((s, q) => s + (q.residuo > 0 ? q.residuo : 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader title="Incassi" subtitle="Pagamenti associati ai preventivi accettati e agli appuntamenti">
        <Button onClick={openManual} data-testid="btn-nuovo-incasso" className="font-semibold">
          <Plus className="mr-1.5 h-4 w-4" /> Nuovo Incasso
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <Card className="p-5 rounded-xl border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between">
            <div><p className="text-xs uppercase tracking-wider text-slate-500">Totale incassato</p>
              <p className="mt-2 font-heading text-3xl font-bold font-mono-data text-emerald-600">€ {data.totale_incassato.toLocaleString("it-IT")}</p></div>
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-100"><Euro className="h-5 w-5 text-emerald-600" /></div>
          </div>
        </Card>
        <Card className="p-5 rounded-xl border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between">
            <div><p className="text-xs uppercase tracking-wider text-slate-500">Residuo preventivi</p>
              <p className="mt-2 font-heading text-3xl font-bold font-mono-data text-amber-600">€ {residuoTotale.toLocaleString("it-IT")}</p></div>
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-100"><TrendingUp className="h-5 w-5 text-amber-600" /></div>
          </div>
        </Card>
        <Card className="p-5 rounded-xl border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between">
            <div><p className="text-xs uppercase tracking-wider text-slate-500">Movimenti</p>
              <p className="mt-2 font-heading text-3xl font-bold font-mono-data">{data.movimenti.length}</p></div>
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10"><CreditCard className="h-5 w-5 text-primary" /></div>
          </div>
        </Card>
      </div>

      {/* Preventivi accettati */}
      <div>
        <h2 className="font-heading text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <FileCheck className="h-5 w-5 text-primary" /> Preventivi accettati
        </h2>
        {loading ? (
          <div className="text-sm text-muted-foreground py-6">Caricamento...</div>
        ) : preventivi.length === 0 ? (
          <EmptyState icon={FileCheck} title="Nessun preventivo accettato"
            description="Quando un paziente accetta un preventivo, comparirà qui per registrare gli incassi." />
        ) : (
          <Card className="overflow-hidden border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead>Numero</TableHead>
                  <TableHead>Paziente</TableHead>
                  <TableHead className="text-right">Totale</TableHead>
                  <TableHead className="text-right">Incassato</TableHead>
                  <TableHead className="text-right">Residuo</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preventivi.map((q) => (
                  <TableRow key={q.id} data-testid={`incasso-quote-${q.id}`} className="hover:bg-slate-50/60">
                    <TableCell className="font-mono-data text-sm font-medium">{q.numero}</TableCell>
                    <TableCell className="font-medium text-slate-800">{q.cliente_nome}</TableCell>
                    <TableCell className="text-right font-mono-data">€ {q.totale.toLocaleString("it-IT")}</TableCell>
                    <TableCell className="text-right font-mono-data text-emerald-600">€ {q.incassato.toLocaleString("it-IT")}</TableCell>
                    <TableCell className="text-right font-mono-data font-semibold" style={{ color: q.residuo > 0 ? "#D97706" : "#10B981" }}>
                      € {q.residuo.toLocaleString("it-IT")}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant={q.residuo > 0 ? "default" : "outline"} onClick={() => openFromQuote(q)}
                              data-testid={`registra-incasso-${q.id}`} disabled={q.residuo <= 0}>
                        <Wallet className="mr-1.5 h-4 w-4" /> {q.residuo > 0 ? "Registra incasso" : "Saldato"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      {/* Movimenti */}
      <div>
        <h2 className="font-heading text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-primary" /> Movimenti
        </h2>
        {data.movimenti.length === 0 ? (
          <EmptyState icon={CreditCard} title="Nessun movimento"
            description="Registra un incasso da un preventivo accettato o conferma il pagamento di un appuntamento." />
        ) : (
          <Card className="overflow-hidden border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead>Data</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Paziente</TableHead>
                  <TableHead className="hidden md:table-cell">Riferimento</TableHead>
                  <TableHead className="hidden sm:table-cell">Metodo</TableHead>
                  <TableHead className="text-right">Importo</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.movimenti.map((m) => (
                  <TableRow key={`${m.tipo}-${m.id}`} data-testid={`movimento-${m.id}`} className="hover:bg-slate-50/60">
                    <TableCell className="text-sm text-muted-foreground">{m.data ? format(new Date(m.data), "d MMM yyyy", { locale: it }) : "—"}</TableCell>
                    <TableCell><span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${TIPO_CLS[m.tipo]}`}>{m.tipo}</span></TableCell>
                    <TableCell className="font-medium text-slate-800">{m.cliente_nome || "—"}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-slate-600 font-mono-data">{m.descrizione}</TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-slate-600">{m.metodo_pagamento || "—"}</TableCell>
                    <TableCell className="text-right font-mono-data font-semibold">€ {m.importo.toLocaleString("it-IT")}</TableCell>
                    <TableCell className="text-right">
                      {m.deletable ? (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" data-testid={`delete-movimento-${m.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Eliminare questo incasso?</AlertDialogTitle>
                              <AlertDialogDescription>€ {m.importo} · {m.cliente_nome}. L'operazione non può essere annullata.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annulla</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(m.id)} className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      ) : <span className="text-[11px] text-slate-400">da Calendario</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Registra Incasso</DialogTitle>
            <DialogDescription>
              {form?.quote_id ? `Preventivo ${form.numero} · ${form.cliente_nome}` : "Registra un incasso manuale"}
            </DialogDescription>
          </DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              {!form.quote_id && (
                <div>
                  <Label>Paziente *</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="incasso-client-select"><SelectValue placeholder="Seleziona paziente" /></SelectTrigger>
                    <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.cognome} {c.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Importo (€) *</Label><Input type="number" step="0.01" value={form.importo} onChange={(e) => setForm((f) => ({ ...f, importo: e.target.value }))} data-testid="incasso-importo-input" className="mt-1.5" /></div>
                <div><Label>Data</Label><Input type="date" value={form.data} onChange={(e) => setForm((f) => ({ ...f, data: e.target.value }))} className="mt-1.5" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Metodo</Label>
                  <Select value={form.metodo_pagamento} onValueChange={(v) => setForm((f) => ({ ...f, metodo_pagamento: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="incasso-metodo-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{methods.map((m) => <SelectItem key={m.id} value={m.nome}>{m.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Modalità</Label>
                  <Select value={form.modalita_pagamento} onValueChange={(v) => setForm((f) => ({ ...f, modalita_pagamento: v }))}>
                    <SelectTrigger className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} rows={2} className="mt-1.5" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                <Button type="submit" disabled={saving} data-testid="save-incasso-button">{saving ? "Salvataggio..." : "Registra"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
